<template>
<header id="heads">
    <div class="nav-shell">
        <nav ref="navigation" class="desktop-navigation" aria-label="Основная навигация">
            <span
                class="nav-pill"
                :class="{ 'nav-pill-ready': pillReady }"
                :style="pillStyle"
                aria-hidden="true"
            ></span>

            <router-link
                to="/"
                class="nav-item unselectable"
                :class="{ 'nav-item-active': activeNavKey === 'home' }"
                data-nav-key="home"
            >Главная</router-link>
            <router-link
                to="/catalog"
                class="nav-item unselectable"
                :class="{ 'nav-item-active': activeNavKey === 'catalog' }"
                data-nav-key="catalog"
            >Каталог</router-link>
            <router-link
                to="/library"
                class="nav-item unselectable"
                :class="{ 'nav-item-active': activeNavKey === 'library' }"
                data-nav-key="library"
            >Библиотека</router-link>
            <router-link
                to="/schedule"
                class="nav-item unselectable"
                :class="{ 'nav-item-active': activeNavKey === 'schedule' }"
                data-nav-key="schedule"
            >Расписание</router-link>
            <router-link
                v-if="isAnimeRoute"
                :to="$route.fullPath"
                class="nav-item nav-item-anime unselectable"
                :class="{ 'nav-item-active': activeNavKey === 'anime' }"
                data-nav-key="anime"
                :title="animeFullTitle"
                :aria-label="`Текущее аниме: ${animeFullTitle}`"
            >{{ animeNavTitle }}</router-link>
            <button
                ref="searchButton"
                type="button"
                class="nav-item nav-button unselectable"
                data-nav-key="search"
                @click="openSearch"
            >Поиск</button>
        </nav>

        <searchbar ref="searchbar" :open="searchOpen" @close="closeSearch"/>
    </div>
</header>
</template>

<script>
import { mapGetters } from 'vuex'
import searchbar from '@/components/searchbar.vue'
import { cleanAnimeTitle, formatAnimeNavTitle } from '@/utils/animeTitle.js'
import { mediaReferenceFromRoute, mediaReferenceKey } from '@/utils/media.js'

export default {
    name: 'Header',
    components: { searchbar },
    data() {
        return {
            searchOpen: this.$route.meta.nav === 'search',
            pillReady: false,
            pillStyle: {
                opacity: 0,
                transform: 'translate3d(0, 0, 0)',
                width: '0px'
            },
            navigationObserver: null
        }
    },
    computed: {
        ...mapGetters(['get_kodik_title', 'get_media_details', 'get_media_details_key']),
        isAnimeRoute() {
            return this.$route.name === 'AnimeView'
                || (this.$route.name === 'MediaView' && this.$route.params.kind === 'anime')
        },
        loadedAnimeTitle() {
            if (!this.isAnimeRoute) return ''
            if (this.$route.name === 'AnimeView') {
                if (String(this.get_kodik_title['id']) !== String(this.$route.query.id)) return ''
                return this.get_kodik_title['title'] || ''
            }
            let expectedKey = mediaReferenceKey(mediaReferenceFromRoute(this.$route))
            return this.get_media_details_key === expectedKey
                ? this.get_media_details && this.get_media_details.title || ''
                : ''
        },
        animeFullTitle() {
            return cleanAnimeTitle(this.loadedAnimeTitle) || 'Аниме'
        },
        animeNavTitle() {
            return formatAnimeNavTitle(this.loadedAnimeTitle, 32) || 'Аниме'
        },
        activeNavKey() {
            if (this.isAnimeRoute) return 'anime'
            let navKey = this.$route.meta.desktopNav || this.$route.meta.nav
            return navKey === 'search' ? null : (navKey || 'home')
        }
    },
    watch: {
        activeNavKey() {
            this.$nextTick(this.updatePill)
        },
        animeNavTitle() {
            this.$nextTick(this.updatePill)
        },
        '$route.fullPath'() {
            this.searchOpen = this.$route.meta.nav === 'search'
            this.$nextTick(this.updatePill)
        }
    },
    mounted() {
        window.addEventListener('resize', this.updatePill)

        if ('ResizeObserver' in window) {
            this.navigationObserver = new ResizeObserver(this.updatePill)
            this.navigationObserver.observe(this.$refs.navigation)
        }

        this.$nextTick(() => {
            this.updatePill()
            if (this.searchOpen) this.focusSearch()
            requestAnimationFrame(() => {
                this.pillReady = true
            })
        })
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updatePill)
        if (this.navigationObserver) this.navigationObserver.disconnect()
    },
    methods: {
        openSearch() {
            this.searchOpen = true
            this.$nextTick(this.focusSearch)
        },
        async closeSearch() {
            this.searchOpen = false
            if (this.$route.meta.nav === 'search') {
                await this.$router.replace('/')
                requestAnimationFrame(() => {
                    let searchButton = document.querySelector('#heads [data-nav-key="search"]')
                    if (searchButton) searchButton.focus()
                })
                return
            }
            this.$nextTick(() => {
                if (this.$refs.searchButton) this.$refs.searchButton.focus()
            })
        },
        focusSearch() {
            if (this.$refs.searchbar) this.$refs.searchbar.focusInput()
        },
        updatePill() {
            let navigation = this.$refs.navigation
            if (!navigation || !this.activeNavKey) {
                this.pillStyle = { ...this.pillStyle, opacity: 0 }
                return
            }

            let activeItem = navigation.querySelector(`[data-nav-key="${this.activeNavKey}"]`)
            if (!activeItem) {
                this.pillStyle = { ...this.pillStyle, opacity: 0 }
                return
            }

            let navigationRect = navigation.getBoundingClientRect()
            let itemRect = activeItem.getBoundingClientRect()
            this.pillStyle = {
                opacity: 1,
                transform: `translate3d(${itemRect.left - navigationRect.left}px, 0, 0)`,
                width: `${itemRect.width}px`
            }
        }
    }
}
</script>

<style scoped>
#heads {
    width: 100%;
    position: absolute;
    z-index: 15;
    height: 80px;
    display: flex;
    top: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0));
}
.nav-shell {
    position: relative;
    width: max-content;
    max-width: calc(100% - 48px);
    height: 52px;
    margin: 14px auto;
}
.desktop-navigation {
    position: relative;
    display: flex;
    align-items: stretch;
    height: 52px;
    padding: 4px;
    box-sizing: border-box;
    border-radius: 999px;
    background: rgba(10, 10, 10, 0.18);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
}
.nav-pill {
    position: absolute;
    z-index: 0;
    top: 4px;
    left: 0;
    height: 44px;
    border-radius: 999px;
    background: #fff;
    pointer-events: none;
    will-change: transform, width;
}
.nav-pill-ready {
    transition:
        transform 240ms cubic-bezier(0.22, 0.61, 0.36, 1),
        width 240ms cubic-bezier(0.22, 0.61, 0.36, 1),
        opacity 140ms ease;
}
.nav-item {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 22px;
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.72);
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: color 150ms ease;
}
.nav-item:hover {
    color: #fff;
}
.nav-item-active,
.nav-item-active:hover {
    color: #090909;
}
.nav-button {
    appearance: none;
    border: 0;
    background: transparent;
}
.nav-item-anime {
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
}

@media (max-width: 1000px) {
    #heads {
        display: none;
    }
}
@media (prefers-reduced-motion: reduce) {
    .nav-pill-ready,
    .nav-item {
        transition: none;
    }
}
</style>
